import { motion } from "framer-motion";
import { ABOUT_IMAGE, TEAM, SITE } from "@/lib/siteData";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

export const About = () => {
  return (
    <section id="about" data-testid="about-snippet-section" className="section-pad relative bg-background">
      <div className="container-site">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <p className="kicker mb-3">About</p>
            <h2 className="font-display uppercase text-foreground text-[clamp(2rem,5vw,4rem)] leading-[0.95] text-balance">
              Storytellers based in <span className="text-[hsl(var(--primary))]">Cape Town.</span>
            </h2>
            <p className="mt-6 text-sm sm:text-base text-muted-foreground leading-relaxed">
              Over a decade of craft, hundreds of productions, and a quiet obsession with how a single frame can move a brand forward. We combine creative direction with disciplined execution - so the work lands every time.
            </p>
            <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
              Award-winning filmmakers, cinematographers and storytellers - working with brands across South Africa, Europe, the UK and the Middle East.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid="about-cta-button"
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                className="h-11 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-5 text-sm"
              >
                Work with us <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <a
                data-testid="about-channel-link"
                href={SITE.socials.youtube}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex h-11 items-center rounded-full border border-border bg-card/60 px-5 text-sm text-foreground hover:bg-card transition-colors"
              >
                Our channel
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <div className="relative overflow-hidden rounded-2xl border border-border">
              <img
                src={ABOUT_IMAGE}
                alt="Pie in the Sky Productions crew on a Cape Town video production location"
                className="w-full h-[320px] sm:h-[420px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 text-white">
                <div className="font-mono-credits text-[10px] uppercase tracking-[0.3em] text-white/70">On set</div>
                <div className="font-display text-2xl uppercase tracking-[0.02em]">Essential Flavours - Cape Town</div>
              </div>
            </div>

            <div className="mt-10">
              <p className="kicker mb-4">The team</p>
              <div data-testid="team-grid" className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {TEAM.map((m) => (
                  <div key={m.name} data-testid={`team-card-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="group">
                    <div className="aspect-[4/5] overflow-hidden rounded-xl border border-border bg-card">
                      <img
                        src={m.image}
                        alt={m.name}
                        loading="lazy"
                        className="h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-[1.04]"
                      />
                    </div>
                    <div className="mt-3">
                      <div className="font-display text-base uppercase tracking-[0.02em] text-foreground">{m.name}</div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">{m.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
